/***************************************************************************************
 * FileName      : TextLabelConfigService.ts
 * Description   : 텍스트 라벨 배치 설정 서비스
 *                 - assets/json/text/*.json에서 각 장비별 텍스트 라벨 배치 정보 로드
 *                 - TextLabelJsonEntry 인터페이스 기반 데이터 제공
 *                 - 장비 ID → 텍스트 라벨 목록 매핑
 * Author        : (주)한국공학기술연구원 박은정
 * Created Date  : 2026.02.12
 * Modified Date :
 * Reference     : packages/scjeil-smart_ict_lab-app/assets/json/text/*.json, src/services/MonitorConfigService.ts
 ***************************************************************************************/

// 2026.02.12 : 박은정 : 텍스트 라벨 JSON 파일 import (장비별)
import circulationTextData from '../../assets/json/text/circulation-text.json';
import distillationTextData from '../../assets/json/text/distillation-text.json';
import heatExchangerTextData from '../../assets/json/text/head-exchanger-text.json';

// 2026.02.12 : 박은정 : 텍스트 라벨 타입 import
import {
  TextLabelJsonEntry,
  TextLabelState,
  DEFAULT_LABEL_WIDTH,
  DEFAULT_LABEL_HEIGHT,
  DEFAULT_LABEL_FONT_SIZE,
  DEFAULT_LABEL_TEXT_COLOR,
  DEFAULT_LABEL_BG_COLOR,
} from '../types/quad-text';

// ============================================================================
// 2026.02.12 : 박은정 : 장비별 텍스트 라벨 JSON 매핑
// ============================================================================

// 2026.02.12 : 박은정 : 각 장비 ID에 대응하는 텍스트 라벨 JSON 데이터 매핑
const textLabelMap: Record<string, TextLabelJsonEntry[]> = {};

// 2026.02.12 : 박은정 : JSON 파일 데이터를 장비 ID 키로 등록
[
  { equipmentId: 'circulation', data: circulationTextData },
  { equipmentId: 'distillation', data: distillationTextData },
  { equipmentId: 'heat-exchanger', data: heatExchangerTextData },
].forEach(({ equipmentId, data }) => {
  textLabelMap[equipmentId] = data as unknown as TextLabelJsonEntry[];
});

// ============================================================================
// TextLabelConfigService 클래스
// ============================================================================

/**
 * 2026.02.12 : 박은정 : 텍스트 라벨 배치 설정 데이터 서비스 (싱글톤)
 * - assets/json/text/*.json에서 로드한 텍스트 라벨 설정 제공
 * - 장비 ID로 조회 가능
 * - TextLabelJsonEntry → TextLabelState 변환 유틸리티 제공
 */
export class TextLabelConfigService {
  private static instance: TextLabelConfigService;

  private constructor() {}

  // 2026.02.12 : 박은정 : 싱글톤 패턴
  static getInstance(): TextLabelConfigService {
    if (!TextLabelConfigService.instance) {
      TextLabelConfigService.instance = new TextLabelConfigService();
    }
    return TextLabelConfigService.instance;
  }

  // 2026.02.12 : 박은정 : 장비 ID로 텍스트 라벨 JSON 목록 조회
  getTextLabels(equipmentId: string): TextLabelJsonEntry[] {
    return textLabelMap[equipmentId] || [];
  }

  // 2026.02.12 : 박은정 : 전체 장비의 텍스트 라벨 목록 조회
  getAllTextLabels(): Record<string, TextLabelJsonEntry[]> {
    return { ...textLabelMap };
  }

  // 2026.02.12 : 박은정 : 전체 장비의 텍스트 라벨을 평탄화하여 반환
  getAllTextLabelsFlat(): TextLabelJsonEntry[] {
    const result: TextLabelJsonEntry[] = [];
    for (const labels of Object.values(textLabelMap)) {
      result.push(...labels);
    }
    return result;
  }

  // 2026.02.12 : 박은정 : 텍스트 라벨이 있는 장비 ID 목록 조회
  getEquipmentIdsWithLabels(): string[] {
    return Object.keys(textLabelMap).filter(
      (id) => textLabelMap[id].length > 0,
    );
  }

  // 2026.02.12 : 박은정 : 장비별 텍스트 라벨 수 조회
  getLabelCount(equipmentId: string): number {
    return (textLabelMap[equipmentId] || []).length;
  }

  // 2026.02.12 : 박은정 : 전체 텍스트 라벨 수 조회
  getTotalLabelCount(): number {
    let count = 0;
    for (const labels of Object.values(textLabelMap)) {
      count += labels.length;
    }
    return count;
  }

  // 2026.02.12 : 박은정 : TextLabelJsonEntry → TextLabelState 변환 (런타임 편집용 상태 생성)
  // 2026.04.23 : 박은정 : UV 기반 엔트리는 anchorPosition이 없으므로 기본값(0,0,0)으로 대체
  toTextLabelState(entry: TextLabelJsonEntry): TextLabelState {
    return {
      id: entry.id,
      label: entry.label,
      text: entry.text || entry.label,
      anchorPosition: entry.anchorPosition ? { ...entry.anchorPosition } : { x: 0, y: 0, z: 0 },
      offset: { x: 0, y: 0, z: 0 },
      rotation: entry.rotation ? { ...entry.rotation } : { x: 0, y: 0, z: 0 },
      width: DEFAULT_LABEL_WIDTH,
      height: DEFAULT_LABEL_HEIGHT,
      fontSize: entry.fontSize || DEFAULT_LABEL_FONT_SIZE,
      textColor: entry.textColor || DEFAULT_LABEL_TEXT_COLOR,
      backgroundColor: entry.backgroundColor || DEFAULT_LABEL_BG_COLOR,
      isPlaced: false,
    };
  }

  // 2026.02.12 : 박은정 : 장비별 TextLabelState 배열 생성
  getTextLabelStates(equipmentId: string): TextLabelState[] {
    const entries = this.getTextLabels(equipmentId);
    return entries.map((entry) => this.toTextLabelState(entry));
  }
}

